import { useId, useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionMetricType,
  FieldImperativeHandle,
} from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import {
  FormattedCurrencyInput,
  NumberInput,
  PercentInput,
  useFieldVariantProps,
  Input,
} from "@/components/organisms/Field/Field.utils";
import { EnumColors } from "@/constants/values";
import { Utils } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import { typographyStyles } from "@/components/ui/typography";

function PopperContent({ metric, unit, isPositive, label }) {
  const { startingValue, endingValue, delta, startDate, endDate } =
    metric || {};

  // Calculate the total duration and current position for timeline visualization
  const getTimelineData = () => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    const totalDuration = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    const progress = Math.max(0, Math.min(1, elapsed / totalDuration));

    return {
      start,
      end,
      now,
      totalDuration,
      elapsed,
      progress,
      isActive: now >= start && now <= end,
      isCompleted: now > end,
      isFuture: now < start,
    };
  };

  const timelineData = getTimelineData();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px",
        minWidth: 240,
      }}
    >
      {/* Top row with title and percent change */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "32px",
        }}
      >
        <div
          style={{
            ...(typographyStyles["title-md"] as React.CSSProperties),
          }}
        >
          {label}
        </div>
        <SimpleEnum
          label={
            Math.abs(parseFloat(String(delta))) < 0.01
              ? "0%"
              : unit === "percent"
                ? `${isPositive ? "+" : "-"}${Utils.fPercent(delta)}`
                : parseFloat(startingValue) !== 0
                  ? `${isPositive ? "+" : "-"}${Utils.fPercent((parseFloat(String(delta)) / parseFloat(startingValue)) * 100)}`
                  : "N/A"
          }
          enumColor={
            Math.abs(parseFloat(String(delta))) < 0.01
              ? EnumColors.GREY_1
              : isPositive
                ? EnumColors.GREEN
                : EnumColors.RED
          }
        />
      </div>

      {/* Timeline visualization */}
      {timelineData && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              color:
                "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            }}
          >
            <span>
              {timelineData.isFuture
                ? "Starts"
                : timelineData.isCompleted
                  ? "Started"
                  : "Started"}
            </span>
            <span>
              {timelineData.isFuture
                ? "Ends"
                : timelineData.isCompleted
                  ? "Ended"
                  : "Ends"}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: 4,
              backgroundColor: "var(--border)",
              borderRadius: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: `${timelineData.progress * 100}%`,
                backgroundColor: timelineData.isActive
                  ? "var(--primary)"
                  : timelineData.isCompleted
                    ? "var(--primary)"
                    : "var(--border)",
                transition: "width 0.3s ease",
              }}
            />
            {timelineData.isActive && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: `${timelineData.progress * 100}%`,
                  height: "100%",
                  width: 2,
                  backgroundColor: "var(--primary)",
                  transform: "translateX(-50%)",
                }}
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ...(typographyStyles["body-xs"] as React.CSSProperties),
              color:
                "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            }}
          >
            <span>{Utils.fDate(timelineData.start)}</span>
            <span>{Utils.fDate(timelineData.end)}</span>
          </div>
        </div>
      )}

      {/* Values section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              color:
                "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            }}
          >
            Starting Value
          </span>
          <span
            style={{
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              fontWeight: 600,
            }}
          >
            {Utils.fCurrency(startingValue)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              color:
                "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            }}
          >
            Current Value
          </span>
          <span
            style={{
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              fontWeight: 600,
            }}
          >
            {Utils.fCurrency(endingValue)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              color:
                "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            }}
          >
            Change
          </span>
          <span
            style={{
              ...(typographyStyles["body-sm"] as React.CSSProperties),
              fontWeight: 600,
              color: isPositive ? "var(--primary-foreground)" : "white",
            }}
          >
            {isPositive ? "+" : "-"}
            {Utils.fCurrency(Math.abs(parseFloat(String(delta))))}
          </span>
        </div>
      </div>
    </div>
  );
}

function FieldMetricValue<T extends FieldDefinitionMetricType<T>>({
  events,
  value,
  mode = "currency",
  fieldVariant,
  style,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionMetricType<T>> & {
  mode?: "currency" | "number" | "percent";
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const { variant, color } = useFieldVariantProps(fieldVariant);
  const { metric, unit, isPositive, label } = value;
  const { endingValue } = metric || {};
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        inputRef.current?.focus();
      },
      unsetFocus: () => {
        inputRef.current?.blur();
      },
    }),
    [],
  );
  let startDecorator = null;
  if (unit === "percent" || unit === "relative") {
    startDecorator = (
      <div
        style={{
          display: "flex",
          transform: "scale(0.8)",
          fontWeight: "normal !important",
        }}
      >
        <SimpleEnum
          label={
            <Icon
              ReactIcon={isPositive ? TrendingUpIcon : TrendingDownIcon}
              size="xs"
              style={{
                transform: "translateY(2px) scale(1.7)",
                transformOrigin: "center",
                marginLeft: "4px",
                marginRight: "4px",
              }}
            />
          }
          enumColor={isPositive ? EnumColors.GREEN : EnumColors.RED}
        />
      </div>
    );
  }

  const isZero = parseFloat(endingValue) === 0;
  const component =
    {
      currency: FormattedCurrencyInput,
      number: NumberInput,
      percent: PercentInput,
    }[mode] || Input;

  const defaultPlaceholder =
    {
      currency: "$44.00",
      number: "43 or 32,771.9483",
      percent: "12.5%",
    }[mode] || "";

  const modeStyle =
    {
      currency: {
        fontWeight: undefined,
      },
      number: {
        fontWeight: undefined,
      },
    }[mode] || {};

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    events?.onChangeField(e.target.value);
  };

  const handleBlur = () => {
    events?.onSubmitField?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  };

  const isDisabled = !events?.onChangeField;

  const InputComponent = component;

  return (
    <Popper
      anchor={
        <div style={{ position: "relative" }}>
          <InputComponent
            ref={inputRef}
            name={`field-metric-value-${id}`}
            value={endingValue || ""}
            disabled={isDisabled}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={isDisabled ? undefined : defaultPlaceholder}
            data-testid={`field-metric-${label || mode || "value"}`}
            style={
              {
                ...(typographyStyles["body-md"] as React.CSSProperties),
                ...modeStyle,
                color: "var(--foreground)",
                fieldSizing: "content",
                width: "fit-content",
                maxWidth: "100%",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
                border: "none",
                background: "transparent",
                alignSelf: "flex-end",
                textAlign: "right",
                outline: "none",
                ...style,
                ...(isZero && {
                  opacity: 0.5,
                }),
              } as React.CSSProperties
            }
          />
          {startDecorator && (
            <div
              style={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translate(-100%, -50%)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {startDecorator}
            </div>
          )}
        </div>
      }
      placement="top"
      isHoverable
    >
      <PopperContent
        metric={metric}
        unit={unit}
        isPositive={isPositive}
        label={label}
      />
    </Popper>
  );
}

function FieldMetricSkeleton() {
  return <Skeleton variant="text" level="body-md" />;
}

export function FieldMetric<T extends FieldDefinitionMetricType<T>>({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionMetricType<T>> & {
  mode?: "currency" | "number" | "percent";
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldMetricSkeleton />;
  }

  return <FieldMetricValue {...props} ref={ref} />;
}
